'use client';

/* eslint-disable react-refresh/only-export-components */

import { useEffect, useState } from 'react';
import { ArrowUUpLeftIcon, CheckIcon, CircleNotchIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { CATEGORY_PICKER_DRAWER_CONTENT_CLASS } from '@/components/categories/CategoryPickerDrawer';
import { SearchBar } from '@/components/transactions/screen/SearchBar';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { useRefundCandidates } from '@/hooks/queries/useRefundCandidates';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { cn } from '@/lib/utils';
import { refundAmountsDiffer } from '@/lib/transactions/refunds';
import type { TransactionRefundCandidate } from '@/lib/api';
import type { RefundRole } from '@/lib/transactions/types';

/** What's being linked — deliberately thin; callers own their own domain type. */
export interface RefundPickerSubject {
  id: string;
  /** 'expense' → picking the received estorno; 'refund' → picking the expense it cancels. */
  side: RefundRole;
  /** Header line: merchant/description. */
  label: string;
  /** Signed amount (negative = expense). */
  amount: number;
  date: string;
  /**
   * Host-computed `canFlagExpectedRefund` verdict — the drawer's subject is too
   * thin to decide. Only ever true for eligible expense-side subjects; enables
   * the "Reembolso previsto" toggle (together with an `onFlagExpected` handler).
   */
  canFlagExpected?: boolean;
  /**
   * When the subject is an installment EXPENSE, how many later parcelas of the same
   * series the link will also cover (server-side propagation). 0/undefined = none.
   */
  subsequentInstallments?: number;
  /**
   * How much of the subject is still unlinked, in its own role: an expense's uncovered value,
   * or an estorno's unspent budget. Defaults to the full magnitude when absent. The consequence
   * note needs it because coverage accumulates — a R$100 expense already covered by R$60 is
   * COMPLETED by a R$40 estorno.
   */
  uncoveredAmount?: number;
  /**
   * Set only in the "Alterar vínculo" flow: the link about to be re-pointed. Its allocation is
   * released by the relink, so the anchor's remaining capacity — which drives ranking — must not
   * count it, or a fully covered expense would rank all of its own replacements as non-exact.
   */
  relinkingLinkId?: string;
}

export function canSaveRefundLink(params: {
  hasSubject: boolean;
  selectedId: string | null;
  saving: boolean;
  /** "Reembolso previsto" toggle — flagging needs no candidate selection. */
  flagMode?: boolean;
}): boolean {
  if (!params.hasSubject || params.saving) return false;
  if (params.flagMode) return true;
  return !!params.selectedId;
}

/**
 * Footer consequence note. A refund link is an annotation, never a movimentação
 * interna: both rows stay in the totals, and the only effect is that the expense
 * stops counting in "Ritmo de gastos".
 *
 * Both arguments are REMAINING values, not face amounts, because coverage accumulates on both
 * sides: the allocation is `min(expenseRemainder, refundAvailable)`, so the expense ends up
 * fully covered whenever the estorno can supply at least the remainder. That makes an oversized
 * estorno "full" (the surplus stays income) and a R$40 estorno "full" against a R$100 expense
 * that already has R$60 linked.
 */
export function refundLinkConsequenceNote(
  expenseRemainder: number | null,
  refundAvailable: number | null,
): string {
  const partial = expenseRemainder != null
    && refundAvailable != null
    && Math.abs(refundAvailable) < Math.abs(expenseRemainder)
    && refundAmountsDiffer(refundAvailable, expenseRemainder);
  return partial
    ? 'A despesa continua nos totais; só a parte reembolsada sai do seu ritmo de gastos.'
    : 'A despesa continua nos totais, mas sai do seu ritmo de gastos.';
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: RefundPickerSubject | null;
  saving: boolean;
  onSave: (subjectId: string, candidate: TransactionRefundCandidate) => Promise<void> | void;
  /** Review-queue "possível reembolso" escape hatch; the ghost renders only when provided. */
  onNotARefund?: () => void;
  /** "Reembolso previsto" toggle path — offered only with a flag-eligible expense subject. */
  onFlagExpected?: (subjectId: string) => Promise<void> | void;
}

const SEARCH_DEBOUNCE_MS = 250;

function candidateDateLabel(date: string): string {
  return new Date(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
}

function CandidateRow({
  candidate,
  side,
  selected,
  disabled,
  onSelect,
}: {
  candidate: TransactionRefundCandidate;
  side: RefundRole;
  selected: boolean;
  disabled: boolean;
  onSelect: (candidate: TransactionRefundCandidate) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const handleSelect = () => onSelect(candidate);
  // An expense anchor lists inflows (estornos); a refund anchor lists outflows.
  const signedAmount = side === 'expense'
    ? Math.abs(candidate.amount)
    : -Math.abs(candidate.amount);
  const partlySpent = candidate.availableAmount != null
    && refundAmountsDiffer(candidate.availableAmount, candidate.amount);

  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        aria-pressed={selected}
        onClick={handleSelect}
        className={cn(
          'flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-xl p-3 text-left transition-colors sm:py-2.5',
          selected ? 'bg-primary/8' : 'hover:bg-surface-sunken',
          disabled && 'cursor-not-allowed opacity-70',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base/6 font-semibold text-text-primary sm:text-sm">
            {candidate.merchantName ?? candidate.description}
          </span>
          <span className="mt-0.5 block truncate text-sm/5 text-text-secondary sm:text-2xs">
            {candidateDateLabel(candidate.date)} · {candidate.accountName}
          </span>
          {/* Partly spent already (both sides fan out): the amount on the right is the row's
              face value, so say what is actually left to allocate. */}
          {partlySpent && (
            <span className="mt-0.5 block truncate text-sm/5 text-text-tertiary sm:text-2xs">
              Resta <MoneyValue text={formatCurrency(candidate.availableAmount!)} />
            </span>
          )}
        </span>
        <span
          className={cn(
            'shrink-0 font-mono text-sm tabular-nums',
            signedAmount >= 0 ? 'text-amount-positive' : 'text-amount-negative',
          )}
        >
          {signedAmount >= 0 ? '+' : ''}
          <MoneyValue text={formatCurrency(signedAmount)} />
        </span>
        <span
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-md border',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong bg-surface',
          )}
          aria-hidden
        >
          {selected && <CheckIcon className="size-3" weight="bold" />}
        </span>
      </button>
    </li>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pt-3 pb-1 font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
      {children}
    </p>
  );
}

function FlagExpectedToggle({
  visible,
  checked,
  disabled,
  onChange,
}: {
  visible: boolean;
  checked: boolean;
  disabled: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  if (!visible) return null;
  return (
    <div className="shrink-0 border-b border-border px-4 py-3 sm:px-5">
      <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-surface-sunken p-3">
        <span className="group mt-0.5 inline-grid size-5 shrink-0 grid-cols-1 sm:size-4">
          <input
            type="checkbox"
            name="flagExpectedRefund"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="col-start-1 row-start-1 appearance-none rounded-sm border border-border-strong bg-surface checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-primary disabled:border-border disabled:bg-surface-sunken forced-colors:appearance-auto"
          />
          <svg
            viewBox="0 0 14 14"
            fill="none"
            className="pointer-events-none col-start-1 row-start-1 size-7/8 self-center justify-self-center stroke-white group-not-has-checked:opacity-0"
            aria-hidden
          >
            <path d="M3 8L6 11L11 3.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm/5 font-semibold text-text-primary sm:text-xs/5">
            Reembolso previsto
          </span>
          <span className="mt-1 block text-sm/5 text-text-secondary sm:text-xs/5">
            O estorno ainda não apareceu? Marque para aguardar — sem vincular nada agora.
          </span>
        </span>
      </label>
    </div>
  );
}

function CandidateListSkeleton() {
  return (
    <ul className="space-y-1 p-1" role="status" aria-busy aria-label="Carregando lançamentos">
      {[0, 1, 2].map((item) => (
        <li key={item} className="h-14 animate-pulse rounded-xl bg-surface-sunken" />
      ))}
    </ul>
  );
}

/**
 * Mirrors CategoryPickerDrawer's shell (bottom sheet ≤768px / desktop right
 * panel) for choosing the counterpart of a refund link. Single-choice; the pair
 * becomes movimentação interna on save.
 */
export function RefundLinkPickerDrawer({
  open,
  onOpenChange,
  subject,
  saving,
  onSave,
  onNotARefund,
  onFlagExpected,
}: Props) {
  const formatCurrency = useFormatCurrency();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flagExpected, setFlagExpected] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebouncedQuery('');
    setSelectedId(null);
    setFlagExpected(false);
  }, [open, subject?.id]);

  const {
    data: pages,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useRefundCandidates(
    open && subject ? subject.id : null,
    debouncedQuery,
    subject?.relinkingLinkId,
  );

  // Paging follows the inputs: a new subject or a settled search term restarts
  // at page one (useSWRInfinite keeps `size` across key changes on its own).
  useEffect(() => {
    void setSize(1);
  }, [debouncedQuery, subject?.id, setSize]);

  // Suggestions are a page-one concept; results accumulate across pages.
  const suggestions = pages?.[0]?.suggestions ?? [];
  const suggestionIds = new Set(suggestions.map((s) => s.transactionId));
  const others = (pages?.flatMap((page) => page.results) ?? [])
    .filter((c) => !suggestionIds.has(c.transactionId));
  const lastPage = pages?.[pages.length - 1];
  const hasMore = Boolean(lastPage?.hasMore);
  const isLoadingMore = isValidating && !!pages && pages.length < size;
  const isEmpty = !isLoading && suggestions.length === 0 && others.length === 0;

  const selectedCandidate =
    suggestions.find((c) => c.transactionId === selectedId)
    ?? others.find((c) => c.transactionId === selectedId)
    ?? null;

  // Both sides accumulate coverage, so the consequence note reasons about what is LEFT on each,
  // not face values. Whichever of subject/candidate is the expense supplies the remainder; the
  // other supplies the available refund budget.
  const subjectRemaining = subject
    ? subject.uncoveredAmount ?? Math.abs(subject.amount)
    : null;
  const candidateAvailable = selectedCandidate
    ? selectedCandidate.availableAmount ?? Math.abs(selectedCandidate.amount)
    : null;
  const expenseRemainder = subject?.side === 'expense' ? subjectRemaining : candidateAvailable;
  const refundAvailable = subject?.side === 'expense' ? candidateAvailable : subjectRemaining;

  const handleLoadMore = () => {
    void setSize(size + 1);
  };

  const flagAvailable = !!subject?.canFlagExpected && !!onFlagExpected;
  const flagMode = flagAvailable && flagExpected;

  const canSave = canSaveRefundLink({
    hasSubject: !!subject,
    selectedId: selectedCandidate ? selectedId : null,
    saving,
    flagMode,
  });

  const handleDrawerOpenChange = (next: boolean) => {
    if (!saving) onOpenChange(next);
  };

  const handleSelectCandidate = (candidate: TransactionRefundCandidate) => {
    setSelectedId((current) => (current === candidate.transactionId ? null : candidate.transactionId));
  };

  const handleFlagModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFlagExpected(event.target.checked);
    // Flagging and linking are mutually exclusive decisions — entering flag
    // mode drops any picked candidate so the CTA can't ambiguously do both.
    if (event.target.checked) setSelectedId(null);
  };

  const handleSave = async () => {
    if (!subject || saving) return;
    if (flagMode) {
      await onFlagExpected?.(subject.id);
      return;
    }
    if (!selectedCandidate) return;
    await onSave(subject.id, selectedCandidate);
  };

  return (
    <Drawer open={open} onOpenChange={handleDrawerOpenChange} repositionInputs={false}>
      <DrawerContent
        data-tokens="v2"
        className={cn(CATEGORY_PICKER_DRAWER_CONTENT_CLASS, 'bg-surface')}
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="shrink-0 px-5 py-4 text-left! group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left!">
          <DrawerTitle className="text-lg font-semibold text-text-primary">
            Vincular reembolso
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            Escolha o lançamento que forma o par de reembolso.
          </DrawerDescription>
          {subject && (
            <div className="mt-2 flex min-w-0 items-center gap-2 text-sm/5 text-text-secondary sm:text-xs">
              <ArrowUUpLeftIcon className="size-4 shrink-0 sm:size-3.5" weight="duotone" />
              <span className="min-w-0 flex-1 truncate">{subject.label}</span>
              <span className="shrink-0 text-text-tertiary">·</span>
              <span className="shrink-0 font-mono tabular-nums">
                <MoneyValue text={formatCurrency(subject.amount)} />
              </span>
              <span className="shrink-0 text-text-tertiary">·</span>
              <span className="shrink-0 font-mono tabular-nums">{candidateDateLabel(subject.date)}</span>
            </div>
          )}
        </DrawerHeader>

        <div className="shrink-0 border-y border-border px-4 py-3 sm:px-5">
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder="Buscar lançamento"
            disabled={saving || flagMode}
          />
        </div>

        <FlagExpectedToggle
          visible={flagAvailable}
          checked={flagExpected}
          disabled={saving}
          onChange={handleFlagModeChange}
        />

        <div
          className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain p-2', flagMode && 'opacity-50')}
          aria-disabled={flagMode || undefined}
        >
          {isLoading ? (
            <CandidateListSkeleton />
          ) : isEmpty ? (
            <div className="px-4 py-8 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-surface-sunken">
                <MagnifyingGlassIcon className="size-5 text-text-tertiary" weight="duotone" />
              </div>
              <p className="mt-3 text-sm font-medium text-text-primary">
                Nenhum lançamento encontrado
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                {subject?.side === 'expense'
                  ? 'Nenhuma entrada compatível nos últimos 90 dias.'
                  : 'Nenhuma despesa compatível nos últimos 90 dias.'}
              </p>
            </div>
          ) : (
            <>
              {suggestions.length > 0 && (
                <>
                  <SectionHeading>Sugestões</SectionHeading>
                  <ul className="space-y-0.5">
                    {suggestions.map((candidate) => (
                      <CandidateRow
                        key={candidate.transactionId}
                        candidate={candidate}
                        side={subject?.side ?? 'expense'}
                        selected={selectedId === candidate.transactionId}
                        disabled={saving || flagMode}
                        onSelect={handleSelectCandidate}
                      />
                    ))}
                  </ul>
                </>
              )}
              {others.length > 0 && (
                <>
                  <SectionHeading>Outros lançamentos · 90 dias</SectionHeading>
                  <ul className="space-y-0.5">
                    {others.map((candidate) => (
                      <CandidateRow
                        key={candidate.transactionId}
                        candidate={candidate}
                        side={subject?.side ?? 'expense'}
                        selected={selectedId === candidate.transactionId}
                        disabled={saving || flagMode}
                        onSelect={handleSelectCandidate}
                      />
                    ))}
                  </ul>
                </>
              )}
              {hasMore && (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={saving || flagMode || isLoadingMore}
                  className="mt-1 flex h-11 w-full cursor-pointer items-center justify-center rounded-xl bg-surface-sunken text-sm font-medium text-text-primary hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
                </button>
              )}
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-border px-4 pt-3 sm:px-5">
          <p className="pb-2 text-2xs text-text-tertiary">
            {flagMode
              ? 'Aguardando estorno — a despesa sai do seu ritmo de gastos até o reembolso chegar ou a flag expirar.'
              : refundLinkConsequenceNote(expenseRemainder, refundAvailable)}
          </p>
          {!flagMode && (subject?.subsequentInstallments ?? 0) > 0 && (
            <p className="pb-2 text-2xs text-accent-brand">
              Se o estorno cobrir o valor total, também vale para as próximas {subject!.subsequentInstallments} parcelas desta série.
            </p>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
          >
            {saving && <CircleNotchIcon className="size-4 animate-spin" weight="bold" />}
            {flagMode ? 'Marcar reembolso previsto' : 'Vincular lançamentos'}
          </button>
          {onNotARefund && (
            <button
              type="button"
              onClick={onNotARefund}
              disabled={saving}
              className="mt-2 flex h-11 w-full cursor-pointer items-center justify-center rounded-xl text-sm font-medium text-text-secondary hover:bg-surface-sunken hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Não é reembolso — manter como entrada
            </button>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
